import { useState, useEffect } from "react";
import { casualData } from "../api/casualData.js";
import { EditableDataTable } from "./EditableDataTable.jsx";
import { useRealtime } from "./realtime/RealtimeProvider.jsx";

// Perkrovus puslapį (F5), skiltis vėl atsidaro ties ta pačia atidaryta lentele.
const ACTIVE_TABLE_KEY = "sannamai_data_matrix_active_table";
function readStoredActiveTable() {
  try { return localStorage.getItem(ACTIVE_TABLE_KEY) || null; } catch { return null; }
}
function storeActiveTable(id) {
  try {
    if (id == null) localStorage.removeItem(ACTIVE_TABLE_KEY);
    else localStorage.setItem(ACTIVE_TABLE_KEY, String(id));
  } catch {}
}

// Kiekviena lentelė — SAVO REST įrašas (žr. price-tables/routes.ts), ne visos
// struktūros PUT, todėl kelios master paskyros gali kurti/trinti nesusijusias
// lenteles vienu metu be konflikto. Pats lentelės turinys (stulpeliai/eilutės)
// redaguojamas EditableDataTable viduje — ten pat vyksta ir gyvas sinchronizavimas.
export function DuomenuMatricaView() {
  const [tables, setTables] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [activeTableId, setActiveTableIdState] = useState(readStoredActiveTable);
  const realtime = useRealtime();

  function setActiveTableId(id) {
    setActiveTableIdState(id);
    storeActiveTable(id);
  }

  useEffect(() => {
    let cancelled = false;
    casualData.listDataMatrixTables()
      .then(data => { if (!cancelled) setTables(Array.isArray(data) ? data : []); })
      .catch(e => console.error("Failed to load data matrix", e))
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  // Gyvi pakeitimai iš kitų sesijų (naujos/pervardintos/ištrintos lentelės).
  useEffect(() => {
    if (!realtime) return;
    return realtime.subscribe(msg => {
      if (msg.type !== "entity") return;
      if (msg.entity === "table") {
        // "created" čia visada reiškia Duomenų matricos atskirą lentelę —
        // Kainininko grupės lentelės sukūrimas siunčia entity:"group", ne
        // "table" (žr. kainininkas/routes.ts), tad šis sąrašas niekada
        // nepriima svetimos grupės lentelės.
        if (msg.action === "created") {
          setTables(prev => prev.some(t => t.id === msg.table.id) ? prev : [...prev, msg.table]);
        }
        // "updated"/"deleted" ateina iš bendro price-tables endpoint'o, taigi
        // gali atkeliauti ir dėl Kainininko grupės lentelės pervadinimo — tada
        // id tiesiog neatitinka nė vieno šio sąrašo įrašo ir žemiau niekas nepasikeičia.
        if (msg.action === "updated") {
          setTables(prev => prev.map(t => t.id === msg.id ? { ...t, name: msg.name ?? t.name, version: msg.version ?? t.version } : t));
        }
        if (msg.action === "deleted") {
          setTables(prev => prev.filter(t => t.id !== msg.id));
          if (activeTableId === msg.id) setActiveTableId(null);
        }
      }
    });
  }, [realtime, activeTableId]);

  const activeTable = tables.find(t => String(t.id) === String(activeTableId)) || null;

  // EditableDataTable pervardijimas/stulpelių/eilučių keitimas atnaujina TIK
  // savo vietinį state'ą — realtime transliacija praleidžia pačią siunčiančią
  // sesiją (žr. registry.ts broadcast), tad be šio perkrovimo šis sąrašas
  // rodytų pasenusį pavadinimą/stulpelių-eilučių skaičių grįžus iš lentelės.
  async function backToList() {
    setActiveTableId(null);
    try {
      const fresh = await casualData.listDataMatrixTables();
      setTables(Array.isArray(fresh) ? fresh : []);
    } catch (e) { console.error("Failed to refresh data matrix list", e); }
  }

  async function deleteTable(table) {
    if (!window.confirm("Ištrinti lentelę?")) return;
    setTables(prev => prev.filter(t => t.id !== table.id));
    if (activeTableId === table.id) setActiveTableId(null);
    try { await casualData.deleteTable(table.id, table.version); }
    catch (e) { console.error("Failed to delete table", e); }
  }
  async function addTable() {
    try {
      const table = await casualData.createDataMatrixTable("Nauja lentelė");
      setTables(prev => [...prev, table]);
      setActiveTableId(table.id);
    } catch (e) { console.error("Failed to create table", e); }
  }

  if (!loaded) return <p style={{ fontSize: 14, color: "#383853" }}>Kraunama...</p>;

  if (activeTable) {
    return <EditableDataTable tableId={activeTable.id} onBack={backToList} templates={tables} showKainininkasLink />;
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <h1 style={{ fontSize: 18, fontWeight: 700, color: "#1C1C1C" }}>Duomenų matrica</h1>
        <button onClick={addTable} style={{ background: "#735DFF", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          + Nauja lentelė
        </button>
      </div>
      {tables.length === 0 ? (
        <p style={{ fontSize: 13, color: "#9ca3af" }}>Lentelių dar nėra.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {tables.map(t => (
            <div key={t.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 14px" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#1c1c1c" }}>{t.name || "(be pavadinimo)"}</div>
                <div style={{ fontSize: 11, color: "#9ca3af" }}>{t.columnCount} stulpeliai · {t.rowCount} eilutės</div>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <button onClick={() => setActiveTableId(t.id)} style={{ background: "#f3f4f6", color: "#383853", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                  Atidaryti
                </button>
                <button onClick={() => deleteTable(t)} style={{ background: "none", border: "none", color: "#d1d5db", cursor: "pointer", fontSize: 16 }}>×</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
